import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import { LogOut } from 'lucide-react'
import { useAuth } from '@/lib/AuthContext'
import { Telemetry } from '@/lib/telemetry'

/**
 * Combined 1Day Sooner + Renaissance Philanthropy partnership lockup.
 *
 * Per Brian (2026-05-06): use the combined lockup as-is rather than the
 * Renaissance-only placeholder originally planned. Source asset at
 * commons/public/partnership-lockup.png. Aspect ratio ~5:1, so at h-7
 * the rendered width is ~140px — fits the header without crowding.
 */
function PartnershipLockup({ className = '' }: { className?: string }) {
  return (
    <img
      src="/partnership-lockup.png"
      alt="1Day Sooner and Renaissance Philanthropy"
      className={`h-7 w-auto select-none ${className}`}
      draggable={false}
    />
  )
}

interface CommonsHeaderProps {
  /** Whether to render the user-facing nav + sign-out controls. */
  variant: 'authenticated' | 'public'
}

function PartnershipSubline({ className = '' }: { className?: string }) {
  // Header subline is now just "powered by Chrona" — full partnership
  // attribution (1Day Sooner + Renaissance Philanthropy) lives in the
  // body's "The partnership" paragraph and the partnership lockup PNG.
  // Repeating partner names in the header was visual noise.
  return (
    <span
      className={`inline-flex items-center text-[11px] text-slate-500 ${className}`}
    >
      powered by&nbsp;
      <a
        href="https://chronabio.ai"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          Telemetry.outboundClicked({
            destination: 'chronabio_pro',
            placement: 'header',
          })
        }
        className="text-slate-600 hover:text-brand-orange transition-colors"
      >
        Chrona
      </a>
    </span>
  )
}

/**
 * The shared CTDCommons header. Thin (h-16), white, light bottom
 * border. Left: wordmark + powered-by subline + Renaissance lockup.
 * Right: text nav + orange "Create account" CTA. On signed-in pages
 * the nav is replaced with the user's email + sign-out.
 */
function CommonsHeader({ variant }: CommonsHeaderProps) {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const handleSignOut = async () => {
    await signOut()
    navigate('/', { replace: true })
  }

  return (
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-4">
        <Link
          to="/"
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <span className="text-lg font-semibold tracking-tight text-brand-charcoal">
            CTDCommons
          </span>
          <PartnershipLockup className="hidden md:inline-block" />
        </Link>
        <PartnershipSubline className="hidden lg:inline-flex" />

        <div className="flex-1" />

        {variant === 'public' && (
          <nav className="flex items-center gap-5 text-sm text-slate-600">
            <Link
              to="/#archive"
              className="hidden sm:inline hover:text-brand-charcoal transition-colors"
            >
              Browse
            </Link>
            <Link
              to="/learn/fda-decoder"
              className="hidden sm:inline hover:text-brand-charcoal transition-colors"
            >
              Learn
            </Link>
            <Link
              to="/auth/login"
              className="hidden sm:inline hover:text-brand-charcoal transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/auth/signup"
              onClick={() =>
                Telemetry.createAccountCtaClicked({ placement: 'header' })
              }
              className="px-3 py-1.5 bg-brand-orange text-white text-sm font-medium rounded-md hover:bg-brand-orange/90 transition-colors"
            >
              Create account
            </Link>
          </nav>
        )}

        {variant === 'authenticated' && user && (
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className={`hidden sm:inline text-sm transition-colors ${
                location.pathname === '/'
                  ? 'text-brand-charcoal font-medium'
                  : 'text-slate-600 hover:text-brand-charcoal'
              }`}
            >
              Portfolios
            </Link>
            <span className="text-xs text-slate-500 hidden sm:inline">
              {user.email}
            </span>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-orange transition-colors"
              title="Sign out"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        )}
      </div>
    </header>
  )
}

/**
 * The shared CTDCommons footer. Mirrors chronabio.ai's minimal shape:
 * partnership statement on top, thin link row, then the tagline.
 */
function CommonsFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-600 space-y-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
          <a
            href="https://chronabio.ai/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-brand-charcoal transition-colors"
          >
            Privacy
          </a>
          <span aria-hidden>·</span>
          <a
            href="https://chronabio.ai/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-brand-charcoal transition-colors"
          >
            Terms
          </a>
          <span aria-hidden>·</span>
          <a
            href="https://chronabio.ai/support"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-brand-charcoal transition-colors"
          >
            Support
          </a>
          <span aria-hidden>·</span>
          <a
            href="mailto:contact@chronabio.ai"
            className="hover:text-brand-charcoal transition-colors"
          >
            contact@chronabio.ai
          </a>
        </div>

        <p className="text-xs text-slate-400 italic">
          Chrona reads. Humans write.
        </p>
      </div>
    </footer>
  )
}

interface AppShellProps {
  children: ReactNode
}

/**
 * Chrome wrapping authenticated CTDCommons pages. Header switches into
 * authenticated mode (email + sign out) automatically; the public
 * variant is used by AuthLayout for unauthenticated landing surfaces.
 */
export function AppShell({ children }: AppShellProps) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <CommonsHeader variant="authenticated" />
      <main className="flex-1">{children}</main>
      <CommonsFooter />
    </div>
  )
}

/** Public-variant header for the unauthenticated chrome (login/signup/etc). */
export function PublicHeader() {
  return <CommonsHeader variant="public" />
}

/** Public footer reused by AuthLayout. */
export function PublicFooter() {
  return <CommonsFooter />
}

/**
 * Backwards-compat shim — the previous `AuthFooter` was a tiny "Powered
 * by Chrona" link. Kept as an alias to the new full footer so any
 * surface that still imports it keeps working without touching it.
 *
 * Prefer `PublicFooter` in new code.
 */
export const AuthFooter = PublicFooter
